import { PortableText } from "@portabletext/react";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { useEffect, useRef, useState } from "react";
import CustomLink from "../../common/Link";
import { useSpring, animated } from "@react-spring/web";
import { withDemotedH1 } from "../../../utils/portableTextComponents";

const Career = ({ data }) => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [sectionTop, setSectionTop] = useState(0);

  // Parallax spring animations with immediate response
  const [{ scrollY }, setScrollY] = useSpring(() => ({
    scrollY: 0,
    immediate: true,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      clamp: true,
    },
  }));

  // Reduced parallax movement for more subtle effect
  const backgroundSpring = useSpring({
    transform: scrollY.to(
      (y) => `translate3d(0, ${isInView ? y * -0.1 : 0}px, 0)`
    ),
    immediate: true,
  });

  const contentSpring = useSpring({
    transform: scrollY.to(
      (y) => `translate3d(0, ${isInView ? y * 0.05 : 0}px, 0)`
    ),
    immediate: true,
  });

  // Handle scroll with RAF for smooth performance
  useEffect(() => {
    let frame = 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        if (entry.isIntersecting) {
          // Store the section's top position relative to viewport when it comes into view
          const rect = entry.boundingClientRect;
          setSectionTop(rect.top + window.scrollY);
        }
      },
      {
        threshold: 0.1, // Trigger when at least 10% of the element is visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const handleScroll = () => {
      if (!isInView) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // Calculate scroll position relative to the section
        const relativeScroll = Math.max(0, window.scrollY - sectionTop);
        setScrollY({ scrollY: relativeScroll });
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(frame);
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [setScrollY, isInView, sectionTop]);

  return (
    <div
      ref={sectionRef}
      className="min-h-[685px] md:min-h-[70vh] relative grid-layout px-section py-section overflow-hidden"
      id={data?.sectionId ?? ""}
    >
      {/* Background Image with Parallax */}
      <animated.div
        className="absolute top-0 left-0 w-full h-full z-[-1]"
        style={backgroundSpring}
      >
        <GatsbyImage
          image={data.image.image.asset.gatsbyImageData}
          alt={data.image.altText}
          objectFit="cover"
          className="object-cover object-center w-full h-[110%]"
        />
      </animated.div>

      {/* Content Container with inverse parallax */}
      <animated.div
        className="z-20 col-span-full flex-row self-start md:self-center items-stretch justify-stretch grid-layout"
        style={contentSpring}
      >
        <div className="max-md:mb-4 w-full md:w-max py-8 px-10 border border-mist rounded-xl max-md:h-max max-md:gap-2 col-span-3 flex flex-col justify-between bg-[#05040b6e] backdrop-blur-[1px]">
          {data.caption && (
            <p className="max-md:hidden text-mist standard-type-eyebrow pt-2 mb-4">
              {data.caption}
            </p>
          )}
          <h2 className="text-mist standard-type-h4 max-md:text-[50px] max-md:leading-[55px] font-flecha">
            {data.title}
          </h2>
          {data.contentSplit !== "two" && (
            <>
              <div className="text-mist wysiwyg wysiwyg-xl mb-4 mt-4 text-wrap-pretty">
                <PortableText
                  value={data._rawDescription}
                  components={withDemotedH1({
                    marks: {
                      link: ({ value, children }) => {
                        const target = (value?.href || "").startsWith("http")
                          ? "_blank"
                          : undefined;
                        return (
                          <a
                            href={value?.href}
                            target={target}
                            className="text-white"
                            rel={target === "_blank" && "noindex nofollow"}
                          >
                            {children}
                          </a>
                        );
                      },
                    },
                  })}
                />
              </div>
              {data.enableButton && (
                <CustomLink
                  cls="px-[24px] h-[37px] gap-1 w-fit flex items-center justify-center leading-none relative uppercase rounded-lg bg-logo1 hover:bg-[#EFEFEF] mt-auto"
                  data={data.button}
                >
                  <span
                    className="font-light text-black"
                    style={{ letterSpacing: "0.1em" }}
                  >
                    {data.button?.buttonText}
                  </span>
                  <svg
                    width={25}
                    height={25}
                    viewBox="0 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-black"
                  >
                    <g clipPath="url(#clip0_0_4)">
                      <path
                        d="M13.9491 9.42736V8.68492H13.7016V8.43744H13.9491V8.18996H14.1966V7.695H14.6915V7.44752H15.6815V7.20004H15.1865V6.95256H14.1966V7.20004H13.7016V7.695H13.4541V7.94248H13.2067V7.44752H12.9592V7.695H12.7117V6.95256H12.9592V6.70508H12.4642V6.95256H12.2167V7.695H12.4642V8.18996H12.7117V8.43744H12.9592V8.68492H13.2067V8.9324H13.4541V9.42736H13.9491ZM12.2167 8.18996V7.695H11.9693V6.95256H11.7218V6.70508H11.4743V7.44752H10.9793V7.20004H10.4844V7.44752H10.7318V7.695H10.9793V7.94248H11.2268V8.18996H12.2167ZM10.2369 6.95256V6.70508H9.98941V6.95256H10.2369ZM10.4844 7.20004V6.95256H10.2369V7.20004H10.4844ZM15.6815 11.9022V11.6547H15.434V11.4072H15.6815V11.6547H15.9289V11.9022H16.6714V11.6547H16.4239V11.4072H16.1764V11.1597H15.9289V10.9122H15.6815V11.1597H15.434V10.9122H15.6815V10.6648H15.434V10.4173H15.1865V9.92232H14.939V9.42736H15.1865V9.17988H15.434V8.9324H16.4239V9.17988H16.9189V9.42736H17.1663V9.67484H17.4138V9.92232H17.6613V10.1698H17.9088V9.67484H17.6613V9.42736H17.4138V9.17988H17.1663V8.9324H16.6714V8.43744H16.4239V8.18996H15.9289V7.94248H14.939V8.18996H14.6915V8.43744H14.4441V8.9324H14.1966V9.42736H13.9491V9.67484H14.1966V9.92232H14.4441V10.1698H14.6915V10.4173H14.939V10.6648H15.1865V10.9122H14.6915V10.6648H14.4441V10.9122H13.9491V11.1597H14.1966V11.4072H14.6915V11.6547H15.1865V11.9022H15.6815ZM12.4642 8.43744V8.18996H12.2167V8.43744H12.4642ZM14.1966 10.6648V10.1698H13.9491V9.92232H13.4541V9.42736H12.9592V9.17988H12.2167V8.9324H11.9693V8.43744H11.7218V8.68492H11.4743V9.17988H11.7218V9.42736H11.9693V9.67484H12.4642V9.92232H12.7117V10.1698H13.2067V10.4173H13.9491V10.6648H14.1966ZM16.1764 15.8619V15.6144H16.4239V15.3669H16.6714V15.1194H16.9189V14.8719H17.1663V14.6245H17.4138V14.377H17.6613V14.1295H17.9088V13.882H18.1563V13.6345H18.4037V13.387H18.6512V13.1396H18.8987V12.8921H19.1462V12.6446H19.3937V12.3971H19.1462V12.1496H18.8987V11.9022H18.6512V11.6547H18.4037V11.4072H18.1563V11.1597H17.9088V10.9122H17.6613V10.6648H17.4138V10.4173H17.1663V10.1698H16.9189V9.92232H16.6714V9.67484H16.4239V9.42736H16.1764V9.17988H15.9289V9.92232H16.1764V10.1698H16.4239V10.4173H16.6714V10.6648H16.9189V10.9122H17.1663V11.1597H17.4138V11.4072H17.6613V11.6547H17.9088V11.9022H18.1563V12.1496H18.4037V12.3971H18.6512V12.6446H18.4037V12.8921H18.1563V13.1396H17.9088V13.387H17.6613V13.6345H17.4138V13.882H17.1663V14.1295H16.9189V14.377H16.6714V14.6245H16.4239V14.8719H16.1764V15.1194H15.9289V15.8619H16.1764ZM18.1563 10.4173V10.1698H17.9088V10.4173H18.1563ZM16.1764 14.8719V14.6245H16.4239V14.377H16.6714V14.1295H16.9189V13.882H17.1663V13.6345H17.4138V13.387H17.6613V13.1396H17.9088V12.8921H18.1563V12.6446H18.4037V12.3971H18.1563V12.1496H17.9088V11.9022H17.6613V11.6547H17.4138V11.4072H17.1663V11.1597H16.9189V10.9122H16.6714V10.6648H16.4239V10.4173H16.1764V10.1698H15.9289V10.6648H16.1764V11.1597H16.4239V11.4072H16.6714V11.6547H16.9189V11.9022H17.1663V12.1496H17.4138V12.3971H17.6613V12.6446H17.4138V12.8921H17.1663V13.1396H16.9189V13.387H16.6714V13.6345H16.4239V13.882H16.1764V14.377H15.9289V14.8719H16.1764ZM15.1865 13.1396V12.6446H16.4239V12.8921H16.6714V12.6446H16.9189V12.8921H17.1663V12.6446H17.4138V12.3971H17.1663V12.1496H16.9189V12.3971H16.6714V12.1496H16.4239V12.3971H15.1865V11.9022H6.42473V13.1396H15.1865ZM14.1966 12.3971V12.1496H14.4441V12.3971H14.1966ZM13.2067 12.3971V12.1496H13.4541V12.3971H13.2067ZM12.2167 12.3971V12.1496H12.4642V12.3971H12.2167ZM11.2268 12.3971V12.1496H11.4743V12.3971H11.2268ZM10.2369 12.3971V12.1496H10.4844V12.3971H10.2369ZM14.1966 12.8921V12.6446H14.4441V12.8921H14.1966ZM13.2067 12.8921V12.6446H13.4541V12.8921H13.2067ZM12.2167 12.8921V12.6446H12.4642V12.8921H12.2167ZM11.2268 12.8921V12.6446H11.4743V12.8921H11.2268ZM10.2369 12.8921V12.6446H10.4844V12.8921H10.2369ZM15.9289 17.0993V16.8518H16.4239V16.6043H16.6714V16.1093H17.1663V15.8619H17.4138V15.6144H17.6613V15.3669H17.9088V14.8719H17.6613V15.1194H17.4138V15.3669H17.1663V15.6144H16.9189V15.8619H16.4239V16.1093H15.434V15.8619H15.1865V15.6144H14.939V15.1194H15.1865V14.6245H15.434V14.377H15.6815V14.1295H15.434V13.882H15.6815V14.1295H15.9289V13.882H16.1764V13.6345H16.4239V13.387H16.6714V13.1396H15.9289V13.387H15.6815V13.6345H15.434V13.387H15.6815V13.1396H15.1865V13.387H14.6915V13.6345H14.1966V13.882H13.9491V14.1295H14.4441V14.377H14.6915V14.1295H15.1865V14.377H14.939V14.6245H14.6915V14.8719H14.4441V15.1194H14.1966V15.3669H13.9491V15.6144H14.1966V16.1093H14.4441V16.6043H14.6915V16.8518H14.939V17.0993H15.9289ZM11.9693 16.6043V16.1093H12.2167V15.8619H12.9592V15.6144H13.4541V15.1194H13.9491V14.8719H14.1966V14.377H13.9491V14.6245H13.2067V14.8719H12.7117V15.1194H12.4642V15.3669H11.9693V15.6144H11.7218V15.8619H11.4743V16.3568H11.7218V16.6043H11.9693ZM18.1563 14.8719V14.6245H17.9088V14.8719H18.1563ZM12.9592 18.3367V18.0892H12.7117V17.3467H12.9592V17.5942H13.2067V17.0993H13.4541V17.3467H13.7016V17.8417H14.1966V18.0892H15.1865V17.8417H15.6815V17.5942H14.6915V17.3467H14.1966V16.8518H13.9491V16.6043H13.7016V16.3568H13.9491V15.6144H13.4541V16.1093H13.2067V16.3568H12.9592V16.6043H12.7117V16.8518H12.4642V17.3467H12.2167V18.0892H12.4642V18.3367H12.9592ZM12.4642 16.8518V16.6043H12.2167V16.8518H12.4642ZM11.7218 18.3367V18.0892H11.9693V17.3467H12.2167V16.8518H11.2268V17.0993H10.9793V17.3467H10.7318V17.5942H10.4844V17.8417H10.9793V17.5942H11.4743V18.3367H11.7218ZM10.4844 18.0892V17.8417H10.2369V18.0892H10.4844ZM10.2369 18.3367V18.0892H9.98941V18.3367H10.2369Z"
                        fill="currentColor"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_0_4">
                        <rect
                          width={13}
                          height="11.6316"
                          fill="white"
                          transform="translate(6.39446 6.70508)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </CustomLink>
              )}
            </>
          )}
        </div>
        {data.contentSplit === "two" && (
          <div className="z-20 flex flex-col md:self-end items-start justify-start w-full col-span-4 md:col-start-6 md:col-span-4 p-8 border border-mist bg-[#05040b6e] rounded-xl overflow-hidden backdrop-blur-[1px]">
            <div className="text-mist wysiwyg wysiwyg-xl mb-4 text-wrap-pretty">
              <PortableText
                value={data._rawDescription}
                components={withDemotedH1({
                  marks: {
                    link: ({ value, children }) => {
                      const target = (value?.href || "").startsWith("http")
                        ? "_blank"
                        : undefined;
                      return (
                        <a
                          href={value?.href}
                          target={target}
                          className="text-white"
                          rel={target === "_blank" && "noindex nofollow"}
                        >
                          {children}
                        </a>
                      );
                    },
                  },
                })}
              />
            </div>
            {data.enableButton && (
              <CustomLink
                cls="px-[24px] h-[37px] gap-1 flex items-center justify-center leading-none relative uppercase rounded-lg bg-logo1 hover:bg-[#EFEFEF] mt-auto"
                data={data.button}
              >
                <span
                  className="font-light text-black"
                  style={{ letterSpacing: "0.1em" }}
                >
                  {data.button?.buttonText}
                </span>
                <svg
                  width={25}
                  height={25}
                  viewBox="0 0 25 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-black"
                >
                  <g clipPath="url(#clip0_0_4)">
                    <path
                      d="M13.9491 9.42736V8.68492H13.7016V8.43744H13.9491V8.18996H14.1966V7.695H14.6915V7.44752H15.6815V7.20004H15.1865V6.95256H14.1966V7.20004H13.7016V7.695H13.4541V7.94248H13.2067V7.44752H12.9592V7.695H12.7117V6.95256H12.9592V6.70508H12.4642V6.95256H12.2167V7.695H12.4642V8.18996H12.7117V8.43744H12.9592V8.68492H13.2067V8.9324H13.4541V9.42736H13.9491ZM12.2167 8.18996V7.695H11.9693V6.95256H11.7218V6.70508H11.4743V7.44752H10.9793V7.20004H10.4844V7.44752H10.7318V7.695H10.9793V7.94248H11.2268V8.18996H12.2167ZM10.2369 6.95256V6.70508H9.98941V6.95256H10.2369ZM10.4844 7.20004V6.95256H10.2369V7.20004H10.4844ZM15.6815 11.9022V11.6547H15.434V11.4072H15.6815V11.6547H15.9289V11.9022H16.6714V11.6547H16.4239V11.4072H16.1764V11.1597H15.9289V10.9122H15.6815V11.1597H15.434V10.9122H15.6815V10.6648H15.434V10.4173H15.1865V9.92232H14.939V9.42736H15.1865V9.17988H15.434V8.9324H16.4239V9.17988H16.9189V9.42736H17.1663V9.67484H17.4138V9.92232H17.6613V10.1698H17.9088V9.67484H17.6613V9.42736H17.4138V9.17988H17.1663V8.9324H16.6714V8.43744H16.4239V8.18996H15.9289V7.94248H14.939V8.18996H14.6915V8.43744H14.4441V8.9324H14.1966V9.42736H13.9491V9.67484H14.1966V9.92232H14.4441V10.1698H14.6915V10.4173H14.939V10.6648H15.1865V10.9122H14.6915V10.6648H14.4441V10.9122H13.9491V11.1597H14.1966V11.4072H14.6915V11.6547H15.1865V11.9022H15.6815ZM12.4642 8.43744V8.18996H12.2167V8.43744H12.4642ZM14.1966 10.6648V10.1698H13.9491V9.92232H13.4541V9.42736H12.9592V9.17988H12.2167V8.9324H11.9693V8.43744H11.7218V8.68492H11.4743V9.17988H11.7218V9.42736H11.9693V9.67484H12.4642V9.92232H12.7117V10.1698H13.2067V10.4173H13.9491V10.6648H14.1966ZM16.1764 15.8619V15.6144H16.4239V15.3669H16.6714V15.1194H16.9189V14.8719H17.1663V14.6245H17.4138V14.377H17.6613V14.1295H17.9088V13.882H18.1563V13.6345H18.4037V13.387H18.6512V13.1396H18.8987V12.8921H19.1462V12.6446H19.3937V12.3971H19.1462V12.1496H18.8987V11.9022H18.6512V11.6547H18.4037V11.4072H18.1563V11.1597H17.9088V10.9122H17.6613V10.6648H17.4138V10.4173H17.1663V10.1698H16.9189V9.92232H16.6714V9.67484H16.4239V9.42736H16.1764V9.17988H15.9289V9.92232H16.1764V10.1698H16.4239V10.4173H16.6714V10.6648H16.9189V10.9122H17.1663V11.1597H17.4138V11.4072H17.6613V11.6547H17.9088V11.9022H18.1563V12.1496H18.4037V12.3971H18.6512V12.6446H18.4037V12.8921H18.1563V13.1396H17.9088V13.387H17.6613V13.6345H17.4138V13.882H17.1663V14.1295H16.9189V14.377H16.6714V14.6245H16.4239V14.8719H16.1764V15.1194H15.9289V15.8619H16.1764ZM18.1563 10.4173V10.1698H17.9088V10.4173H18.1563ZM16.1764 14.8719V14.6245H16.4239V14.377H16.6714V14.1295H16.9189V13.882H17.1663V13.6345H17.4138V13.387H17.6613V13.1396H17.9088V12.8921H18.1563V12.6446H18.4037V12.3971H18.1563V12.1496H17.9088V11.9022H17.6613V11.6547H17.4138V11.4072H17.1663V11.1597H16.9189V10.9122H16.6714V10.6648H16.4239V10.4173H16.1764V10.1698H15.9289V10.6648H16.1764V11.1597H16.4239V11.4072H16.6714V11.6547H16.9189V11.9022H17.1663V12.1496H17.4138V12.3971H17.6613V12.6446H17.4138V12.8921H17.1663V13.1396H16.9189V13.387H16.6714V13.6345H16.4239V13.882H16.1764V14.377H15.9289V14.8719H16.1764ZM15.1865 13.1396V12.6446H16.4239V12.8921H16.6714V12.6446H16.9189V12.8921H17.1663V12.6446H17.4138V12.3971H17.1663V12.1496H16.9189V12.3971H16.6714V12.1496H16.4239V12.3971H15.1865V11.9022H6.42473V13.1396H15.1865ZM14.1966 12.3971V12.1496H14.4441V12.3971H14.1966ZM13.2067 12.3971V12.1496H13.4541V12.3971H13.2067ZM12.2167 12.3971V12.1496H12.4642V12.3971H12.2167ZM11.2268 12.3971V12.1496H11.4743V12.3971H11.2268ZM10.2369 12.3971V12.1496H10.4844V12.3971H10.2369ZM14.1966 12.8921V12.6446H14.4441V12.8921H14.1966ZM13.2067 12.8921V12.6446H13.4541V12.8921H13.2067ZM12.2167 12.8921V12.6446H12.4642V12.8921H12.2167ZM11.2268 12.8921V12.6446H11.4743V12.8921H11.2268ZM10.2369 12.8921V12.6446H10.4844V12.8921H10.2369ZM15.9289 17.0993V16.8518H16.4239V16.6043H16.6714V16.1093H17.1663V15.8619H17.4138V15.6144H17.6613V15.3669H17.9088V14.8719H17.6613V15.1194H17.4138V15.3669H17.1663V15.6144H16.9189V15.8619H16.4239V16.1093H15.434V15.8619H15.1865V15.6144H14.939V15.1194H15.1865V14.6245H15.434V14.377H15.6815V14.1295H15.434V13.882H15.6815V14.1295H15.9289V13.882H16.1764V13.6345H16.4239V13.387H16.6714V13.1396H15.9289V13.387H15.6815V13.6345H15.434V13.387H15.6815V13.1396H15.1865V13.387H14.6915V13.6345H14.1966V13.882H13.9491V14.1295H14.4441V14.377H14.6915V14.1295H15.1865V14.377H14.939V14.6245H14.6915V14.8719H14.4441V15.1194H14.1966V15.3669H13.9491V15.6144H14.1966V16.1093H14.4441V16.6043H14.6915V16.8518H14.939V17.0993H15.9289ZM11.9693 16.6043V16.1093H12.2167V15.8619H12.9592V15.6144H13.4541V15.1194H13.9491V14.8719H14.1966V14.377H13.9491V14.6245H13.2067V14.8719H12.7117V15.1194H12.4642V15.3669H11.9693V15.6144H11.7218V15.8619H11.4743V16.3568H11.7218V16.6043H11.9693ZM18.1563 14.8719V14.6245H17.9088V14.8719H18.1563ZM12.9592 18.3367V18.0892H12.7117V17.3467H12.9592V17.5942H13.2067V17.0993H13.4541V17.3467H13.7016V17.8417H14.1966V18.0892H15.1865V17.8417H15.6815V17.5942H14.6915V17.3467H14.1966V16.8518H13.9491V16.6043H13.7016V16.3568H13.9491V15.6144H13.4541V16.1093H13.2067V16.3568H12.9592V16.6043H12.7117V16.8518H12.4642V17.3467H12.2167V18.0892H12.4642V18.3367H12.9592ZM12.4642 16.8518V16.6043H12.2167V16.8518H12.4642ZM11.7218 18.3367V18.0892H11.9693V17.3467H12.2167V16.8518H11.2268V17.0993H10.9793V17.3467H10.7318V17.5942H10.4844V17.8417H10.9793V17.5942H11.4743V18.3367H11.7218ZM10.4844 18.0892V17.8417H10.2369V18.0892H10.4844ZM10.2369 18.3367V18.0892H9.98941V18.3367H10.2369Z"
                      fill="currentColor"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_0_4">
                      <rect
                        width={13}
                        height="11.6316"
                        fill="white"
                        transform="translate(6.39446 6.70508)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </CustomLink>
            )}
          </div>
        )}
      </animated.div>
    </div>
  );
};

export default Career;
